/**
 * ReceptionFilters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Box } from '@mui/material';
import { InlineFilterBar, InlineSelect, InlineMultiSelect } from '../../../components/common';
import type { ActiveChip } from '../../../hooks/useFilterState';
import {
  ReceptionFilters as IReceptionFilters,
  AttendanceStatus,
  ATTENDANCE_STATUS_LABELS,
  PERIOD_OPTIONS,
} from '../../../types/reception';

interface ReceptionFiltersProps {
  filters: IReceptionFilters;
  onFilterChange: <K extends keyof IReceptionFilters>(key: K, value: IReceptionFilters[K]) => void;
  onClear: () => void;
  loading?: boolean;
  total?: number;
}

const PERIOD_SELECT_OPTIONS = PERIOD_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

const STATUS_OPTIONS = Object.entries(ATTENDANCE_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export const ReceptionFilters: React.FC<ReceptionFiltersProps> = ({
  filters,
  onFilterChange,
  onClear,
  loading = false,
  total,
}) => {
  // The filter state is owned by the parent's useReception hook (not this
  // component), because it is also mutated externally by the summary cards'
  // status chips. Controls read straight from `filters` instead of keeping
  // a local copy, so both stay in sync.
  const period = filters.period ?? 'all';
  const selectedStatuses: string[] = Array.isArray(filters.attendanceStatus)
    ? filters.attendanceStatus
    : filters.attendanceStatus
    ? [filters.attendanceStatus]
    : [];

  const chips: ActiveChip[] = [];
  if (period !== 'all') {
    const periodLabel = PERIOD_OPTIONS.find((option) => option.value === period)?.label ?? period;
    chips.push({
      key: 'period',
      label: 'Período',
      value: periodLabel,
      onDelete: () => onFilterChange('period', 'all' as IReceptionFilters['period']),
    });
  }
  if (selectedStatuses.length > 0) {
    chips.push({
      key: 'attendanceStatus',
      label: 'Status',
      value: selectedStatuses
        .map((status) => ATTENDANCE_STATUS_LABELS[status as AttendanceStatus] ?? status)
        .join(', '),
      onDelete: () =>
        onFilterChange('attendanceStatus', undefined as unknown as IReceptionFilters['attendanceStatus']),
    });
  }

  return (
    <Box sx={{ mb: 2 }}>
      <InlineFilterBar
        searchPlaceholder="Buscar por nome ou CPF..."
        searchValue={filters.search ?? ''}
        onSearchChange={(v) => onFilterChange('search', v)}
        resultCount={total}
        resultNoun="atendimentos"
        chips={chips}
        onClearAll={onClear}
        loading={loading}
      >
        <InlineSelect
          label="Período"
          value={period}
          options={PERIOD_SELECT_OPTIONS}
          onChange={(v) => onFilterChange('period', v as IReceptionFilters['period'])}
          minWidth={140}
        />
        <InlineMultiSelect
          label="Status Atendimento"
          value={selectedStatuses}
          options={STATUS_OPTIONS}
          onChange={(v) =>
            onFilterChange(
              'attendanceStatus',
              (v.length > 0 ? v : undefined) as IReceptionFilters['attendanceStatus'],
            )
          }
          minWidth={200}
        />
      </InlineFilterBar>
    </Box>
  );
};

export default ReceptionFilters;
